/**
 * Utility functions for handling image capture, processing and OCR
 * for the TradesPurple Quick Add feature
 *
 * All processing is done locally without storing images in the cloud
 *
 * Image processing pipeline for OCR optimization:
 * 1. Resize to a manageable size for better performance
 * 2. Apply slight blur to reduce noise (pin holes, paper texture)
 * 3. Convert to grayscale using standard luminance formula
 * 4. Apply thresholding to create high-contrast black and white
 * 5. Compress to JPEG for efficient processing
 *
 * These processing steps significantly improve OCR accuracy for
 * business cards and vehicle signage by reducing noise and
 * enhancing text visibility.
 */
import { debug } from '@/lib/utils';

// Import Tesseract.js dynamically to avoid loading it on initial page load
const loadTesseract = async () => {
  try {
    const { createWorker } = await import('tesseract.js');
    return createWorker();
  } catch (error) {
    debug.error('Failed to load Tesseract.js:', error);
    throw new Error('Failed to load text recognition engine');
  }
};

/**
 * Detect if running on iOS Safari
 * @returns boolean indicating if the current browser is iOS Safari
 */
const isIOSSafari = (): boolean => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) &&
         !window.MSStream &&
         /WebKit/.test(ua) &&
         !/Chrome/.test(ua);
};

/**
 * Simplified image processing for iOS Safari
 * Avoids canvas imageData manipulation which can cause issues on iOS
 *
 * @param file The image file to process
 * @param maxWidth Maximum width of the processed image
 * @returns A promise that resolves to a processed image Blob
 */
const compressImageIOSSafari = async (file: File, maxWidth = 1200): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const image = new Image();
      image.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = image.width;
        let height = image.height;

        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
        }

        // Create canvas and context for resizing
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        // Draw the image at the new size
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Simple draw without filters or pixel manipulation for iOS Safari
        ctx.drawImage(image, 0, 0, width, height);

        // Convert to blob with reasonable quality
        try {
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            0.85 // 85% quality provides good balance of quality vs file size
          );
        } catch (error) {
          debug.error('Error converting canvas to blob:', error);
          // Fallback for older browsers or if toBlob fails
          try {
            const dataURL = canvas.toDataURL('image/jpeg', 0.85);
            fetch(dataURL)
              .then(res => res.blob())
              .then(blob => resolve(blob))
              .catch(err => {
                debug.error('Fallback blob conversion failed:', err);
                reject(new Error('Failed to process image'));
              });
          } catch (fallbackError) {
            debug.error('All image conversion methods failed:', fallbackError);
            reject(new Error('Browser does not support image processing'));
          }
        }
      };

      image.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      // Set the source of the image to the reader result
      if (typeof readerEvent.target?.result === 'string') {
        image.src = readerEvent.target.result;
      } else {
        reject(new Error('Failed to read image'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Processes an image file to optimize it for OCR text extraction
 *
 * Processing steps:
 * - Resizes the image to manageable dimensions
 * - Applies a slight blur to reduce noise (pin holes, paper texture)
 * - Converts to grayscale using luminance formula
 * - Applies thresholding for high-contrast black & white
 * - Compresses to reduce file size
 *
 * @param file The image file to process
 * @param maxWidth Maximum width of the processed image
 * @returns A promise that resolves to a processed image Blob
 */
export const compressImage = async (file: File, maxWidth = 1200): Promise<Blob> => {
  // Use simplified processing for iOS Safari
  if (isIOSSafari()) {
    debug.log('Using iOS Safari compatible image processing');
    return compressImageIOSSafari(file, maxWidth);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const image = new Image();
      image.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = image.width;
        let height = image.height;

        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
        }

        // Create canvas and context for resizing
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        // Draw the image at the new size
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        try {
          // Step 1: Apply a slight blur to reduce noise (pin holes, paper texture)
          // Save the original state
          ctx.save();

          // Apply blur filter
          ctx.filter = 'blur(1px)';
          ctx.drawImage(image, 0, 0, width, height);

          // Reset filter and restore context state
          ctx.filter = 'none';
          ctx.restore();

          // Step 2: Convert to grayscale
          const imageData = ctx.getImageData(0, 0, width, height);
          const data = imageData.data;

          for (let i = 0; i < data.length; i += 4) {
            // Use standard luminance formula to convert to grayscale
            const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            data[i] = data[i + 1] = data[i + 2] = gray;
          }

          // Step 3: Apply thresholding to create high contrast black and white
          // This helps distinguish text from background noise
          const threshold = 128; // Middle gray value

          for (let i = 0; i < data.length; i += 4) {
            // Convert to either pure black or pure white based on threshold
            const value = data[i] > threshold ? 255 : 0;
            data[i] = data[i+1] = data[i+2] = value;
            // Alpha channel remains unchanged
          }

          ctx.putImageData(imageData, 0, 0);
        } catch (error) {
          debug.error('Error processing image pixels:', error);
          // If pixel manipulation fails, redraw the image without processing
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(image, 0, 0, width, height);
        }

        // Convert to blob with reasonable quality
        try {
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            0.85 // 85% quality provides good balance of quality vs file size
          );
        } catch (error) {
          debug.error('Error converting canvas to blob:', error);
          // Fallback for older browsers or if toBlob fails
          try {
            const dataURL = canvas.toDataURL('image/jpeg', 0.85);
            fetch(dataURL)
              .then(res => res.blob())
              .then(blob => resolve(blob))
              .catch(err => {
                debug.error('Fallback blob conversion failed:', err);
                reject(new Error('Failed to process image'));
              });
          } catch (fallbackError) {
            debug.error('All image conversion methods failed:', fallbackError);
            reject(new Error('Browser does not support image processing'));
          }
        }
      };

      image.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      // Set the source of the image to the reader result
      if (typeof readerEvent.target?.result === 'string') {
        image.src = readerEvent.target.result;
      } else {
        reject(new Error('Failed to read image'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Create a promise that rejects after a specified timeout
 * @param ms The timeout in milliseconds
 * @returns A promise that rejects after the timeout
 */
const timeout = (ms: number): Promise<never> => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Operation timed out after ${ms}ms`)), ms);
  });
};

/**
 * Run a promise with a timeout
 * @param promise The promise to run
 * @param ms The timeout in milliseconds
 * @returns A promise that resolves with the result or rejects with a timeout error
 */
const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  return Promise.race([promise, timeout(ms)]);
};

/**
 * Extracts text from an image using OCR
 * @param imageFile The image file to process
 * @param timeoutMs Optional timeout in milliseconds (default: 20000ms)
 * @returns A promise that resolves to the extracted text
 */
export const performOCR = async (imageFile: File, timeoutMs = 20000): Promise<string> => {
  try {
    // Compress the image for better performance
    const compressedImage = await withTimeout(compressImage(imageFile), 10000);

    debug.log('Loading Tesseract.js worker');
    // Load Tesseract.js worker
    const worker = await withTimeout(loadTesseract(), 10000);

    debug.log('Initializing OCR engine');
    // Initialize the worker with English language
    await withTimeout(worker.loadLanguage('eng'), 10000);
    await withTimeout(worker.initialize('eng'), 10000);

    debug.log('OCR processing started for image');

    // Set parameters for better results with business cards/van signs
    await worker.setParameters({
      tessedit_ocr_engine_mode: 3, // Legacy + LSTM mode for better results
      preserve_interword_spaces: 1, // Preserve spaces
    });

    // Recognize text in the image
    debug.log('Running OCR recognition');
    const { data } = await withTimeout(worker.recognize(compressedImage), timeoutMs);

    // Terminate the worker to free memory
    await worker.terminate();

    debug.log('OCR processing completed');

    return data.text;
  } catch (error) {
    debug.error('OCR processing error:', error);
    
    // Add specific error message for timeout
    if (error.message && error.message.includes('timed out')) {
      throw new Error('OCR processing took too long. Please try again or enter details manually.');
    }
    
    throw new Error('Failed to extract text from image');
  }
};

// Upload functionality removed - all processing is now local
